import { useState } from "react";
import { IoPlayOutline, IoEllipsisHorizontal } from "react-icons/io5";

import UnpostedEntriesHeader from "./components/UnpostedEntriesHeader";
import UnpostedEntriesTable from "./components/UnpostedEntriesTable";

import Dropdown from "./../../components/common/Dropdown";
import Modal from "../../components/common/Modal";
import JournalForm from "./components/JournalForm";

import journalService from "../../services/journalService";

const UnpostedEntries = () => {
  const [data, setData] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [tableOptions, setTableOptions] = useState({
    reference: "",
    search_query: "",
    current_page: 1,
    size: 10,
    sort_column: { path: "date_issued", order: "asc" },
  });

  const actions = (item) => {
    return [
      [
        {
          name: "Post Entry",
          icon: <IoPlayOutline className="mr-3 text-green-500" />,
          action: () => handleSelect(item),
        },
      ],
    ];
  };

  const columns = [
    { path: "date_issued", label: "Date" },
    { path: "name", label: "Name" },
    { path: "client_name", label: "Client" },
    { path: "net_amount", label: "Net Amount" },
    { path: "reference_type", label: "Reference" },
    {
      key: "actions",
      content: (data) => (
        <Dropdown items={actions(data)} icon={<IoEllipsisHorizontal />} />
      ),
    },
  ];

  const handleSelect = async (item) => {
    if (item) {
      const params = {
        type: "",
        search_query: "",
        current_page: 1,
        size: 200,
        sort_column: { path: "created_on", order: "desc" },
      };

      const items = [];

      const { result } = await journalService.getAccountChartsAsync(params);

      // Official Receipt or 2307
      if (item.reference < 3) {
        const cash = result.filter((i) => i.name === "Cash")[0];

        const cash_entry = {
          account_chart: {
            label: cash.name,
            value: cash.id,
          },
          debit: item.net,
          name: item.client_name,
          description: item.description,
        };

        items.push(cash_entry);

        if (item.withheld > 0) {
          const creditable = result.filter(
            (i) => i.name === "Creditable Withholding Tax"
          )[0];

          const creditable_entry = {
            account_chart: {
              label: creditable.name,
              value: creditable.id,
            },
            debit: item.withheld,
            name: item.client_name,
            description: item.description,
          };

          items.push(creditable_entry);
        }

        if (item.discount > 0) {
          const discount = result.filter(
            (i) => i.name === "Sales/Professional Discount"
          )[0];

          const discount_entry = {
            account_chart: {
              label: discount.name,
              value: discount.id,
            },
            debit: item.discount,
            name: item.client_name,
            description: item.description,
          };

          items.push(discount_entry);
        }

        const income = result.filter(
          (i) => i.name === "Professional Income"
        )[0];

        const income_entry = {
          account_chart: {
            label: income.name,
            value: income.id,
          },
          credit: item.gross,
          name: item.client_name,
          description: item.description,
        };

        items.push(income_entry);
      }

      // Invoice
      if (item.reference === 3) {
        const fee = result.filter((i) => i.name === "Professional Fee")[0];
        const payable = result.filter((i) => i.name === "Accounts Payable")[0];

        const fee_entry = {
          account_chart: {
            label: fee.name,
            value: fee.id,
          },
          debit: item.net,
          name: item.client_name,
          description: item.description,
        };

        const payable_entry = {
          account_chart: {
            label: payable.name,
            value: payable.id,
          },
          credit: item.net,
          name: item.client_name,
          description: item.description,
        };

        items.push(fee_entry);
        items.push(payable_entry);
      }

      // Payment
      if (item.reference === 4) {
        const payable = result.filter((i) => i.name === "Accounts Payable")[0];
        const cash = result.filter((i) => i.name === "Cash")[0];

        const payable_entry = {
          account_chart: {
            label: payable.name,
            value: payable.id,
          },
          debit: item.net,
          name: item.client_name,
          description: item.description,
        };

        const cash_entry = {
          account_chart: {
            label: cash.name,
            value: cash.id,
          },
          credit: item.net,
          name: item.client_name,
          description: item.description,
        };

        items.push(payable_entry);
        items.push(cash_entry);
      }

      setEntries(items);
      setSelected(item);
      setShowForm(true);
      return;
    }

    setShowForm(false);
    setSelected(null);
    setEntries([]);

    return;
  };

  const handleSuccess = () => {
    const new_data = data.filter((i) => i.id !== selected.id);

    setData(new_data);
    setShowForm(false);
    setSelected(null);
    setEntries([]);
  };

  return (
    <div className="px-6 lg:px-0">
      <div className="card">
        <UnpostedEntriesHeader
          options={tableOptions}
          onChange={setTableOptions}
        />
        <UnpostedEntriesTable
          data={data}
          columns={columns}
          options={tableOptions}
          onChange={setTableOptions}
          setData={setData}
        />
      </div>
      <Modal
        size="7xl"
        title="Create Journal Entry"
        isOpen={showForm}
        isLoading={isLoading}
        onClose={handleSelect}
        borderTop
      >
        <JournalForm
          data={selected}
          defaultEntries={entries}
          isLoading={isLoading}
          setLoading={setIsLoading}
          onSuccess={handleSuccess}
        />
      </Modal>
    </div>
  );
};

export default UnpostedEntries;
